import React, { useState, useEffect } from "react";
import Page from "../components/Page";
import {
  IconKey,
  IconLock,
  IconLink,
  IconUnlink,
  IconRefresh,
  IconPlus,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconWifi,
  IconBluetooth,
  IconBattery,
  IconBed,
  IconBuilding,
  IconLayersSubtract,
  IconArrowLeft,
  IconAdjustments
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useRfidLocks,
  syncRfidLocks,
  mapRfidLock,
  unmapRfidLock,
  setRfidLockSector
} from "../controllers/rfid.controller";
import ApiClient from "../helpers/ApiClient";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

export default function RfidLockMappingPage() {
  const { data: locks, error, mutate, isLoading } = useRfidLocks();
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Mapping Modal State
  const [selectedLock, setSelectedLock] = useState(null);
  const [targetRoomId, setTargetRoomId] = useState("");
  const [buildingNo, setBuildingNo] = useState(1);
  const [floorNo, setFloorNo] = useState(1);
  const [mappingSubmitting, setMappingSubmitting] = useState(false);

  // Sector Modal State
  const [sectorLock, setSectorLock] = useState(null);
  const [cardSector, setCardSector] = useState("1");
  const [sectorSubmitting, setSectorSubmitting] = useState(false);

  // Filter
  const [filterMode, setFilterMode] = useState("all"); // 'all', 'mapped', 'unmapped'

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await ApiClient.get("/rooms");
      setRooms(res.data?.data || []);
    } catch (err) {
      console.warn("Failed to fetch rooms:", err);
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncRfidLocks();
      toast.success(res.data?.message || "Locks synchronized successfully!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to sync locks");
    } finally {
      setSyncing(false);
    }
  };

  const openMappingModal = (lock) => {
    setSelectedLock(lock);
    setTargetRoomId(lock.room_id || "");
    setBuildingNo(lock.building_no || 1);
    setFloorNo(lock.floor_no || lock.room_floor || 1);
    document.getElementById("modal-map-lock").showModal();
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    if (!targetRoomId) {
      toast.error("Please select a hotel room to map");
      return;
    }
    setMappingSubmitting(true);
    try {
      const res = await mapRfidLock({
        rfid_lock_id: selectedLock.id,
        room_id: parseInt(targetRoomId),
        building_no: parseInt(buildingNo) || 1,
        floor_no: parseInt(floorNo) || 1,
      });
      toast.success(res.data?.message || "Lock mapped successfully!");
      document.getElementById("modal-map-lock").close();
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to map lock");
    } finally {
      setMappingSubmitting(false);
    }
  };

  const handleUnmap = async (lock) => {
    if (!window.confirm(`Are you sure you want to unmap lock "${lock.lock_name}" from Room ${lock.room_name}?`)) {
      return;
    }
    try {
      await unmapRfidLock(lock.map_id);
      toast.success("Lock unmapped successfully!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to unmap lock");
    }
  };

  const openSectorModal = (lock) => {
    setSectorLock(lock);
    setCardSector("1");
    document.getElementById("modal-set-sector").showModal();
  };

  const handleSaveSector = async (e) => {
    e.preventDefault();
    setSectorSubmitting(true);
    try {
      const res = await setRfidLockSector(sectorLock.id, cardSector);
      toast.success(res.data?.message || "Hotel card sector configured!");
      document.getElementById("modal-set-sector").close();
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to configure card sector");
    } finally {
      setSectorSubmitting(false);
    }
  };

  const lockList = locks || [];
  const filteredLocks = lockList.filter((lk) => {
    if (filterMode === "mapped") return !!lk.room_id;
    if (filterMode === "unmapped") return !lk.room_id;
    return true;
  });

  const mappedCount = lockList.filter((l) => !!l.room_id).length;
  const unmappedCount = lockList.length - mappedCount;

  return (
    <Page className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/rfid-doorlock"
            className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
          >
            <IconArrowLeft size={20} />
          </Link>
          <div className="p-2.5 bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-2xl">
            <IconLink stroke={iconStroke} size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Lock & Room Mapping
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Link Sciener/TTLock smart locks to your eHospitality rooms (1:1 mapping)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-3.5 py-2 text-xs font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 shadow-sm transition disabled:opacity-50"
          >
            <IconRefresh size={16} className={syncing ? "animate-spin" : ""} />
            <span>{syncing ? "Syncing..." : "Sync Locks from Vendor"}</span>
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-neutral-800 p-1 rounded-xl">
          {[
            { id: "all", label: `All Locks (${lockList.length})` },
            { id: "mapped", label: `Mapped (${mappedCount})` },
            { id: "unmapped", label: `Unmapped (${unmappedCount})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilterMode(tab.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                filterMode === tab.id
                  ? "bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-xs"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {unmappedCount > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-950/20 px-3 py-1 rounded-lg border border-amber-200 dark:border-amber-900/30">
            <IconAlertTriangle size={16} />
            <span>{unmappedCount} lock(s) not yet mapped to a room</span>
          </div>
        )}
      </div>

      {/* Lock Grid / Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
        {filteredLocks.length === 0 ? (
          <div className="p-12 text-center">
            <IconLock size={40} className="mx-auto text-gray-400 mb-2" />
            <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
              No locks found
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
              Click "Sync Locks from Vendor" above to import your Sciener/TTLock locks into eHospitality.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">Lock Name / Alias</th>
                  <th className="py-3 px-4">Mapped Room</th>
                  <th className="py-3 px-4">Location</th>
                  <th className="py-3 px-4">Connectivity</th>
                  <th className="py-3 px-4">MAC Address</th>
                  <th className="py-3 px-4">Active Cards</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                {filteredLocks.map((lock) => (
                  <tr key={lock.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition">
                    <td className="py-3 px-4">
                      <div className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                        <IconLock size={15} className="text-gray-400" />
                        <span>{lock.lock_name}</span>
                      </div>
                      {lock.lock_alias && lock.lock_alias !== lock.lock_name && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          Alias: {lock.lock_alias}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400 font-mono">
                        ID: {lock.vendor_lock_id}
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      {lock.room_name ? (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-300 font-bold">
                          <IconBed size={15} />
                          <span>Room {lock.room_name}</span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-950/20 px-2 py-0.5 rounded">
                          <IconAlertTriangle size={12} />
                          <span>Unmapped</span>
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                      <div>Building {lock.building_no || 1}</div>
                      <div className="text-[11px] text-gray-400">Floor {lock.floor_no || lock.room_floor || 1}</div>
                    </td>

                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        {lock.has_gateway === 1 ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 px-2 py-0.5 rounded">
                            <IconWifi size={13} />
                            <span>Gateway</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 bg-blue-50 dark:bg-blue-950/20 px-2 py-0.5 rounded">
                            <IconBluetooth size={13} />
                            <span>Bluetooth</span>
                          </span>
                        )}
                        {lock.electric_quantity !== null && (
                          <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500" title={`Battery: ${lock.electric_quantity}%`}>
                            <IconBattery size={13} />
                            <span>{lock.electric_quantity}%</span>
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-3 px-4 font-mono text-gray-500 text-[11px]">
                      {lock.lock_mac || "—"}
                    </td>

                    <td className="py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">
                      <span className="px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300">
                        {lock.active_cards_count || 0} active
                      </span>
                    </td>

                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openSectorModal(lock)}
                          className="px-2 py-1 rounded-lg border border-gray-200 dark:border-neutral-700 hover:bg-gray-100 text-gray-600 dark:text-gray-300 text-[11px] flex items-center gap-1"
                          title="Set Hotel Card Sector"
                        >
                          <IconAdjustments size={13} />
                          <span>Sector</span>
                        </button>

                        {lock.room_id ? (
                          <>
                            <button
                              onClick={() => openMappingModal(lock)}
                              className="px-2.5 py-1 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-[11px] font-semibold"
                            >
                              Remap
                            </button>
                            <button
                              onClick={() => handleUnmap(lock)}
                              className="p-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
                              title="Unmap Room"
                            >
                              <IconUnlink size={15} />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => openMappingModal(lock)}
                            className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold flex items-center gap-1"
                          >
                            <IconLink size={13} />
                            <span>Map Room</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL: MAP LOCK TO ROOM */}
      <dialog id="modal-map-lock" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-white dark:bg-neutral-800 text-gray-900 dark:text-white max-w-md">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-neutral-700">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <IconLink size={18} className="text-emerald-600" />
              <span>Map Lock to Room</span>
            </h3>
            <button
              onClick={() => document.getElementById("modal-map-lock").close()}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700"
            >
              <IconX size={16} />
            </button>
          </div>

          <form onSubmit={handleSaveMapping} className="mt-4 space-y-4 text-xs">
            <div className="p-3 bg-gray-50 dark:bg-neutral-900/50 rounded-xl border">
              <div className="text-[11px] text-gray-500">Target Lock</div>
              <div className="font-bold text-sm text-gray-900 dark:text-white mt-0.5">
                {selectedLock?.lock_name}
              </div>
              <div className="text-[11px] font-mono text-gray-400">
                MAC: {selectedLock?.lock_mac || "—"} • ID: {selectedLock?.vendor_lock_id}
              </div>
            </div>

            <div>
              <label className="block font-semibold mb-1">
                Select Hotel Room *
              </label>
              <select
                value={targetRoomId}
                onChange={(e) => {
                  setTargetRoomId(e.target.value);
                  const rm = rooms.find(r => String(r.id) === String(e.target.value));
                  if (rm && rm.floor) setFloorNo(parseInt(rm.floor) || 1);
                }}
                className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                required
              >
                <option value="">-- Choose Room --</option>
                {rooms.map((rm) => (
                  <option key={rm.id} value={rm.id}>
                    Room {rm.name} ({rm.room_type_name || "Standard"}{rm.floor ? `, Floor ${rm.floor}` : ""})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold mb-1">Building #</label>
                <input
                  type="number"
                  min="1"
                  value={buildingNo}
                  onChange={(e) => setBuildingNo(parseInt(e.target.value) || 1)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Floor #</label>
                <input
                  type="number"
                  min="1"
                  value={floorNo}
                  onChange={(e) => setFloorNo(parseInt(e.target.value) || 1)}
                  className="w-full p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                />
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200 dark:border-neutral-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-map-lock").close()}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 dark:hover:bg-neutral-700 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mappingSubmitting}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
              >
                {mappingSubmitting ? "Saving..." : "Confirm Mapping"}
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* MODAL: SET HOTEL CARD SECTOR */}
      <dialog id="modal-set-sector" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-white dark:bg-neutral-800 text-gray-900 dark:text-white max-w-sm">
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-neutral-700">
            <h3 className="font-bold text-sm">Configure Hotel Card Sector</h3>
            <button
              onClick={() => document.getElementById("modal-set-sector").close()}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <IconX size={16} />
            </button>
          </div>

          <form onSubmit={handleSaveSector} className="mt-4 space-y-3 text-xs">
            <p className="text-gray-500 dark:text-gray-400">
              Set the MIFARE card sector on lock <b>{sectorLock?.lock_name}</b> so that physical keys issued by your USB Encoder are verified directly by the lock.
            </p>

            <div>
              <label className="block font-semibold mb-1">Card Sector Index</label>
              <input
                type="text"
                value={cardSector}
                onChange={(e) => setCardSector(e.target.value)}
                placeholder="Default: 1"
                className="w-full p-2.5 rounded-lg border font-mono text-center text-sm font-bold"
                required
              />
            </div>

            <div className="pt-3 flex justify-end gap-2 border-t">
              <button
                type="button"
                onClick={() => document.getElementById("modal-set-sector").close()}
                className="px-3 py-1.5 rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sectorSubmitting}
                className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
              >
                {sectorSubmitting ? "Configuring..." : "Set Sector"}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </Page>
  );
}
